import React, { useState } from 'react';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import useFormValidation from 'services/hooks/useFormValidation';
import { Button, IconButton, PasswordInput, Stack, Text } from 'shell/components';

const defaultValues = {
    current: '',
    new: '',
    confirmNew: '',
};

const ChangePassword = ({ setSelected }) => {
    const { updatePassword } = useAuth();
    const { message } = useSnackbar();
    const { device } = useViewport();

    const [values, setValues] = useState(defaultValues);
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [currentPasswordError, setCurrentPasswordError] = useState('');

    const testLength = (value) => value.length > 8;
    const testUppercase = (value) => /[A-Z]/.test(value);
    const testLowerCase = (value) => /[a-z]/.test(value);
    const testNumber = (value) => /[0-9]/.test(value);
    const testSymbol = (value) => /^(?=.*[!@#$%^&*]).*$/.test(value);

    const getErrorMessage = (key, value, form) => {
        switch (key) {
            case 'new':
                if (
                    testLength(value) &&
                    testUppercase(value) &&
                    testLowerCase(value) &&
                    testNumber(value) &&
                    testSymbol(value)
                ) {
                    return '';
                } else {
                    return 'Satisfy the following requirements:';
                }
            case 'confirmNew':
                return value === form.new ? '' : 'This does not match your new password';
            default:
                return '';
        }
    };

    const { errors, validate, validateAll } = useFormValidation(values, getErrorMessage);

    const handleChange = (e) => {
        setValues((current) => {
            return {
                ...current,
                [e.target.name]: e.target.value,
            };
        });

        if (hasClickedSubmit || errors[e.target.name]) {
            validate(e.target.name, e.target.value, values);
        }
    };

    const handleClick = async () => {
        if (!hasClickedSubmit) setHasClickedSubmit(true);
        if (!validateAll()) return;

        setIsLoading(true);
        const result = await updatePassword(values.current, values.new);
        setIsLoading(false);
        if (result) {
            message.success('Your password has been updated.');
            setValues(defaultValues);
            setHasClickedSubmit(false);
            setCurrentPasswordError('');
        } else {
            setCurrentPasswordError('Your current password is incorrect.');
        }
    };

    const getColor = (requirement) => {
        switch (requirement) {
            case 'length':
                return testLength(values.new) ? 'success' : 'light';
            case 'uppercase':
                return testUppercase(values.new) ? 'success' : 'light';
            case 'lowercase':
                return testLowerCase(values.new) ? 'success' : 'light';
            case 'number':
                return testNumber(values.new) ? 'success' : 'light';
            case 'symbol':
                return testSymbol(values.new) ? 'success' : 'light';
            default:
                return;
        }
    };

    return (
        <CustomStack device={device} spacing={4}>
            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                {device === 'mobile' && (
                    <IconButton
                        aria-label={'back'}
                        kind={'arrowLeft'}
                        size={'medium'}
                        variant={'square'}
                        onClick={() => setSelected('')}
                    />
                )}
                <Text variant={'sh2'} weight={'semibold'}>
                    Change Password
                </Text>
            </Stack>
            <PasswordInput
                error={Boolean(currentPasswordError)}
                helperText={currentPasswordError}
                label={'Current Password'}
                name={'current'}
                placeholder={'Current Password'}
                value={values.current}
                onChange={handleChange}
            />
            <PasswordInput
                error={Boolean(errors.new)}
                helperText={errors.new}
                label={'New Password'}
                name={'new'}
                placeholder={'New Password'}
                value={values.new}
                onChange={handleChange}
            />
            <Stack component={'ul'}>
                <Text color={getColor('length')} component={'li'} variant={'p3'}>
                    At least 8 characters
                </Text>
                <Text color={getColor('uppercase')} component={'li'} variant={'p3'}>
                    At least 1 uppercase character
                </Text>
                <Text color={getColor('lowercase')} component={'li'} variant={'p3'}>
                    At least 1 lowercase character
                </Text>
                <Text color={getColor('number')} component={'li'} variant={'p3'}>
                    At least 1 number
                </Text>
                <Text color={getColor('symbol')} component={'li'} variant={'p3'}>
                    At least 1 symbol
                </Text>
            </Stack>
            <PasswordInput
                error={Boolean(errors.confirmNew)}
                helperText={errors.confirmNew}
                label={'Confirm New Password'}
                name={'confirmNew'}
                placeholder={'Confirm New Password'}
                value={values.confirmNew}
                onChange={handleChange}
            />
            <Button sx={{ ml: 'auto' }} loading={isLoading} onClick={handleClick}>
                Save
            </Button>
        </CustomStack>
    );
};

const CustomStack = styled(Stack)(({ device, theme }) => {
    return {
        ...((device === 'tablet' || device === 'desktop') && {
            padding: theme.spacing(5),
            maxWidth: '32rem',
        }),
    };
});

export default ChangePassword;
